import { Sparkles, Type, Timer, RotateCcw, Play, Wand2, Star, HeartPulse, Trophy } from "lucide-react";

import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  useBingoSettingsStore,
  DEFAULT_TIMING,
  TIMING_BOUNDS,
  TIMING_LABELS,
} from "@/plugins/bingo/state/bingoSettingsStore";
import { triggerPreviewReveal } from "@/plugins/bingo/projector/ball/PreviewRevealOverlay";
import { triggerBingoCheckPreview } from "@/plugins/bingo/verification/BingoCheckPreviewOverlay";
import { triggerConfirmedBingoPreview } from "@/plugins/bingo/celebration/ConfirmedBingoPreviewOverlay";
import { PHASE2_MODULES } from "@/plugins/bingo/projector/specials/phase2Specials";
import { SPECIAL_NUMBERS } from "@/plugins/bingo/projector/effects/specialFxConfig";

const TIMING_KEYS = [
  "ballAppearMs",
  "ballSpinMs",
  "ballOpenMs",
  "revealPauseMs",
  "magicTrailMs",
  "boardPulseMs",
];

// Reveal-only stages contribute to the "total number-reveal" duration.
// boardPulseMs runs on the cell AFTER the reveal lands, so it isn't part
// of the number-reveal countdown shown in the header.
const REVEAL_KEYS = TIMING_KEYS.filter((k) => k !== "boardPulseMs");

/**
 * BingoSettingsTab — Bingo Show plugin settings.
 * Sections:
 *   1. Special number effects + nicknames (Phase 1)
 *   2. Reveal timing sliders (Phase 2) — every stage duration is
 *      host-adjustable so pacing can be tuned to a real audience without
 *      touching code.
 *   3. Special-numbers preview
 */
export default function BingoSettingsTab() {
  const specialEffectsEnabled = useBingoSettingsStore(
    (s) => s.settings.specialEffectsEnabled,
  );
  const nicknamesEnabled = useBingoSettingsStore(
    (s) => s.settings.nicknamesEnabled,
  );
  const revealTiming = useBingoSettingsStore((s) => s.settings.revealTiming);
  const specials = useBingoSettingsStore((s) => s.settings.specials);
  const setSpecialEffectsEnabled = useBingoSettingsStore(
    (s) => s.setSpecialEffectsEnabled,
  );
  const setNicknamesEnabled = useBingoSettingsStore(
    (s) => s.setNicknamesEnabled,
  );
  const setSpecialEnabled = useBingoSettingsStore((s) => s.setSpecialEnabled);
  const setRevealTiming = useBingoSettingsStore((s) => s.setRevealTiming);
  const resetRevealTiming = useBingoSettingsStore((s) => s.resetRevealTiming);

  const totalRevealMs = REVEAL_KEYS.reduce(
    (sum, k) => sum + (revealTiming[k] || 0),
    0,
  );

  return (
    <div
      data-testid="settings-plugin-bingo"
      className="mt-6 flex flex-col gap-4"
    >
      <ToggleRow
        icon={<Sparkles className="w-5 h-5" style={{ color: "var(--ic-primary)" }} />}
        title="Special number effects"
        description="Play unique animations and sounds when a special number is called (7, 13, 21, 22, 45, 69, 77, 88, 90)."
        checked={specialEffectsEnabled}
        onChange={setSpecialEffectsEnabled}
        testid="setting-special-effects"
      />

      <ToggleRow
        icon={<Type className="w-5 h-5" style={{ color: "var(--ic-primary)" }} />}
        title="UK bingo nicknames"
        description="Show the traditional nickname under each special number (Lucky Seven, Two Fat Ladies, Top of the Shop, ...)."
        checked={nicknamesEnabled}
        onChange={setNicknamesEnabled}
        testid="setting-nicknames"
      />

      {/* Reveal timing — Phase 2 Step 1 */}
      <div
        data-testid="settings-reveal-timing"
        className="rounded-xl border p-6"
        style={{
          background: "var(--ic-bg-surface)",
          borderColor: "var(--ic-border-subtle)",
        }}
      >
        <div className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center border shrink-0"
              style={{
                background: "var(--ic-bg-elevated)",
                borderColor: "var(--ic-border-subtle)",
              }}
            >
              <Timer
                className="w-5 h-5"
                style={{ color: "var(--ic-primary)" }}
              />
            </div>
            <div>
              <div className="ic-font-display font-semibold">Reveal timing</div>
              <div className="text-xs text-[color:var(--ic-text-muted)] mt-1 max-w-xl">
                Fine-tune every stage of the ball reveal for your audience.
                Changes apply live to the Projector.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="settings-timing-preview"
              type="button"
              onClick={() => triggerPreviewReveal()}
              className="h-9 px-3 rounded-md text-xs font-semibold text-black inline-flex items-center gap-2 hover:brightness-110 active:scale-[0.99]"
              style={{
                background: "var(--ic-primary)",
                boxShadow: "0 0 16px var(--ic-primary-glow)",
              }}
            >
              <Wand2 className="w-3.5 h-3.5" strokeWidth={2.5} />
              Preview Animation
            </button>
            <button
              data-testid="settings-timing-reset"
              type="button"
              onClick={resetRevealTiming}
              className="h-9 px-3 rounded-md text-xs border border-[color:var(--ic-border-subtle)] hover:border-[color:var(--ic-border-focus)] inline-flex items-center gap-2"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset
            </button>
          </div>
        </div>

        {/* Total number-reveal duration read-out */}
        <div
          className="mt-5 mb-6 flex items-center gap-4 px-4 py-3 rounded-lg border"
          style={{
            background: "var(--ic-bg-elevated)",
            borderColor: "color-mix(in srgb, var(--ic-primary) 30%, transparent)",
          }}
        >
          <Play className="w-4 h-4" style={{ color: "var(--ic-primary)" }} />
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--ic-text-muted)]">
              Total number-reveal duration
            </div>
            <div
              className="ic-font-display text-2xl font-bold tabular-nums"
              style={{ color: "var(--ic-primary)" }}
              data-testid="settings-timing-total"
            >
              {(totalRevealMs / 1000).toFixed(2)}s
            </div>
          </div>
          <div className="text-[10px] tracking-widest uppercase text-[color:var(--ic-text-muted)] text-right max-w-[16rem]">
            Design brief target: 2–2.5s.
            <br />
            Board pulse runs after this on the target cell.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
          {TIMING_KEYS.map((k) => (
            <TimingSlider
              key={k}
              tKey={k}
              value={revealTiming[k]}
              onChange={(v) => setRevealTiming({ [k]: v })}
              defaultValue={DEFAULT_TIMING[k]}
            />
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-surface)] p-5">
        <h3 className="ic-font-display text-sm font-semibold tracking-widest uppercase text-[color:var(--ic-text-muted)] mb-4">
          Phase 2 · Special animations
        </h3>
        <div className="flex flex-col gap-3">
          {PHASE2_MODULES.map((m) => (
            <Phase2ModuleCard
              key={m.key}
              module={m}
              enabled={specials?.[m.key] !== false}
              onToggle={(v) => setSpecialEnabled(m.key, v)}
              onPreview={() => {
                triggerPreviewReveal(
                  m.previewValue ? m.previewValue() : undefined,
                );
              }}
            />
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-surface)] p-5">
        <h3 className="ic-font-display text-sm font-semibold tracking-widest uppercase text-[color:var(--ic-text-muted)] mb-4">
          Signature Moments
        </h3>
        <div className="flex flex-col gap-3">
          <SignatureCard
            icon={<HeartPulse className="w-5 h-5" />}
            title="Bingo Check"
            tagline="Heartbeat verification · calm before the celebration"
            onPreview={() => triggerBingoCheckPreview()}
            testid="signature-bingo-check"
          />
          <SignatureCard
            icon={<Trophy className="w-5 h-5" />}
            title="Confirmed Bingo Celebration"
            tagline="Hybrid gold reveal · confetti · brass fanfare · spotlight"
            onPreview={() => triggerConfirmedBingoPreview()}
            testid="signature-confirmed-bingo"
          />
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-surface)] p-5">
        <h3 className="ic-font-display text-sm font-semibold tracking-widest uppercase text-[color:var(--ic-text-muted)] mb-4">
          Special numbers (legacy)
        </h3>
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-3">
          {Object.entries(SPECIAL_NUMBERS).map(([v, cfg]) => (
            <div
              key={v}
              data-testid={`special-number-${v}`}
              className="rounded-lg border p-3 flex flex-col items-center gap-2"
              style={{
                borderColor: "color-mix(in srgb, var(--ic-primary) 30%, transparent)",
                background: "var(--ic-bg-elevated)",
              }}
            >
              <span
                className="ic-font-display text-2xl font-bold"
                style={{
                  color: "var(--ic-primary)",
                  textShadow: "0 0 12px var(--ic-primary-glow)",
                }}
              >
                {v}
              </span>
              <span className="text-[10px] tracking-widest uppercase text-center text-[color:var(--ic-text-muted)] leading-tight">
                {cfg.nickname}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Phase2ModuleCard({ module: mod, enabled, onToggle, onPreview }) {
  return (
    <div
      data-testid={`phase2-module-${mod.key}`}
      className="rounded-lg border p-4 flex items-center justify-between gap-4"
      style={{
        background: "var(--ic-bg-elevated)",
        borderColor: enabled
          ? "color-mix(in srgb, var(--ic-primary) 45%, transparent)"
          : "var(--ic-border-subtle)",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: enabled
              ? "color-mix(in srgb, var(--ic-primary) 20%, transparent)"
              : "var(--ic-bg-surface)",
            border: "1px solid var(--ic-border-subtle)",
            color: enabled ? "var(--ic-primary)" : "var(--ic-text-muted)",
          }}
        >
          <Star className="w-5 h-5" fill={enabled ? "currentColor" : "none"} />
        </div>
        <div>
          <div className="ic-font-display font-semibold">{mod.label}</div>
          <div className="text-[10px] tracking-widest uppercase text-[color:var(--ic-text-muted)] mt-0.5">
            {mod.tagline}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid={`phase2-preview-${mod.key}`}
          type="button"
          onClick={onPreview}
          disabled={!enabled}
          className="h-9 px-3 rounded-md text-xs font-semibold text-black inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.99]"
          style={{ background: "var(--ic-primary)" }}
        >
          <Wand2 className="w-3.5 h-3.5" strokeWidth={2.5} />
          Preview
        </button>
        <Switch
          data-testid={`phase2-toggle-${mod.key}`}
          checked={enabled}
          onCheckedChange={onToggle}
        />
      </div>
    </div>
  );
}

function SignatureCard({ icon, title, tagline, onPreview, testid }) {
  return (
    <div
      data-testid={`${testid}-card`}
      className="rounded-lg border p-4 flex items-center justify-between gap-4"
      style={{
        background: "var(--ic-bg-elevated)",
        borderColor:
          "color-mix(in srgb, var(--ic-primary) 45%, transparent)",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background:
              "color-mix(in srgb, var(--ic-primary) 20%, transparent)",
            border: "1px solid var(--ic-border-subtle)",
            color: "var(--ic-primary)",
          }}
        >
          {icon}
        </div>
        <div>
          <div className="ic-font-display font-semibold">{title}</div>
          <div className="text-[10px] tracking-widest uppercase text-[color:var(--ic-text-muted)] mt-0.5">
            {tagline}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid={`${testid}-preview`}
          type="button"
          onClick={onPreview}
          className="h-9 px-3 rounded-md text-xs font-semibold text-black inline-flex items-center gap-2 hover:brightness-110 active:scale-[0.99]"
          style={{ background: "var(--ic-primary)" }}
        >
          <Wand2 className="w-3.5 h-3.5" strokeWidth={2.5} />
          Preview
        </button>
      </div>
    </div>
  );
}

function TimingSlider({ tKey, value, onChange, defaultValue }) {
  const b = TIMING_BOUNDS[tKey];
  const label = TIMING_LABELS[tKey];
  const seconds = (value / 1000).toFixed(2);
  const isDefault = value === defaultValue;

  return (
    <label
      data-testid={`timing-slider-${tKey}`}
      className="flex flex-col gap-2"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs tracking-[0.25em] uppercase text-[color:var(--ic-text-secondary)]">
          {label.title}
        </span>
        <span
          className="ic-font-display font-bold tabular-nums"
          style={{ color: isDefault ? "var(--ic-text-primary)" : "var(--ic-primary)" }}
          data-testid={`timing-value-${tKey}`}
        >
          {seconds}s
        </span>
      </div>
      <Slider
        data-testid={`timing-input-${tKey}`}
        value={[value]}
        min={b.min}
        max={b.max}
        step={b.step}
        onValueChange={([v]) => onChange(v)}
      />
      <div className="flex items-center justify-between text-[10px] tracking-widest uppercase text-[color:var(--ic-text-muted)]">
        <span>{label.hint}</span>
        <span className="tabular-nums shrink-0 ml-2">
          {(b.min / 1000).toFixed(2)}–{(b.max / 1000).toFixed(2)}s
        </span>
      </div>
    </label>
  );
}

function ToggleRow({ icon, title, description, checked, onChange, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border p-5 flex items-center justify-between gap-4"
      style={{
        background: "var(--ic-bg-surface)",
        borderColor: "var(--ic-border-subtle)",
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center border shrink-0"
          style={{
            background: "var(--ic-bg-elevated)",
            borderColor: "var(--ic-border-subtle)",
          }}
        >
          {icon}
        </div>
        <div>
          <div className="ic-font-display font-semibold">{title}</div>
          <div className="text-xs text-[color:var(--ic-text-muted)] mt-1 max-w-xl">
            {description}
          </div>
        </div>
      </div>
      <Switch
        data-testid={`${testid}-switch`}
        checked={checked}
        onCheckedChange={onChange}
      />
    </div>
  );
}
